import { Sheet } from "@c4c-web/ui";
import React, { useEffect, useRef, useState } from "react";
import { useBooleanState } from "@c4c-web/libs-hooks/hooks";
// import { EAnalyticsEventLabel, EAnalyticsScreenName } from "../../../constants";
import type { IAddress, ILocation } from "../../../types";
import { AddressForm } from "../AddressForm/AddressForm";
import {
  getUserIsLoggedIn,
  setLatLong,
  setPreferredDeliveryArea,
} from "../../../utils/cookie.utils";
// import { useAddressTracking } from "../../../hooks";
import { formatGoogleAddressDescription } from "./Address.helper";
import { Footer } from "./AddressMapFooter";
import type { IAddressMap } from "./map.interface";
import { MapSearchBar } from "./MapSearchBar";
import { CenteredMapIllustration } from "./CenteredMapIllustration";
import { AddressSelectorContainer } from "./AddressSelectorContainer";

export const AddressMap: React.FC<IAddressMap> = ({
  address = {} as IAddress,
  configStore,
  coordinate = {} as ILocation,
  editInfo = { id: "" },
  id = "",
  open,
  selected = false,
  setShowMap,
  storeId,
  t,
  typing = false,
}): JSX.Element => {
  const { countryNameEn, countryNameAr, countryOfOperationID, profile } =
    configStore;
  const { state: showForm, toggle: toggleAddressForm } = useBooleanState(false);
  const [isTyping, setIsTyping] = useState<boolean>(typing);
  const [isAddressSelected, setIsAddressSelected] = useState<boolean>(selected);
  const [addressInfo, setAddressInfo] = useState<string>("");
  const [emirate, setEmirate] = useState<string>("");
  const [town, setTown] = useState<string>("");
  const [isAreaSupported, setIsAreaSupported] = useState<boolean | null>(null);
  const [coordinates, setCoordinates] = useState<ILocation>({
    lat: configStore.defaultLatitude,
    lng: configStore.defaultLongitude,
  });

  const isUserLoggedIn = getUserIsLoggedIn();
  const searchBarRef = useRef<HTMLInputElement>(null);
  // const { trackEvent } = useAddressTracking(isTyping, isAddressSelected);

  const firstName = profile?.first_name ?? "";
  const contactNumber =
    profile?.contact_number && profile.contact_number.length > 0
      ? profile.contact_number
      : [{ number: "" }];

  const onChangeHandler = (): void => {
    setIsTyping(true);
  };

  const formattedAddress = formatGoogleAddressDescription(
    addressInfo,
    emirate,
    countryNameEn || countryNameAr,
  );

  const handleLoggedOutJourney = (): void => {
    setShowMap((prev) => !prev);
    setPreferredDeliveryArea(formattedAddress, storeId);
    setLatLong(coordinates.lat.toString(), coordinates.lng.toString());
    window.location.reload();
  };

  const handleLoggedInJourney = (): void => {
    setShowMap((prev) => !prev);
    toggleAddressForm();
  };

  const handleDeliveryClick = (): void => {
    isUserLoggedIn ? handleLoggedInJourney() : handleLoggedOutJourney();
    // trackEvent(
    //   EAnalyticsEventLabel.AddressConfirmed,
    //   EAnalyticsScreenName.AddDeliveryLocationUserSearchesForLocation,
    // );
  };

  const handleSetIsTyping = (value: boolean): void => {
    setIsTyping(value);
  };

  const handleSetIsSelected = (value: boolean): void => {
    setIsAddressSelected(value);
  };

  const handleSheetState = (): void => {
    setShowMap((prev) => !prev);
    setIsTyping(false);
    setIsAddressSelected(false);
  };

  const addressData = isUserLoggedIn
    ? {
        ...address,
        emirate,
        country: { isocode: countryOfOperationID },
        firstName,
        phone: contactNumber[0].number || "",
        town,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        streetName: formattedAddress,
        defaultCountryCode: configStore.defaultCountryCode,
      }
    : undefined;

  useEffect(() => {
    setIsTyping(typing);
  }, [typing]);

  useEffect(() => {
    setIsAddressSelected(selected);
  }, [selected]);

  useEffect(() => {
    setCoordinates({
      lat: coordinate.lat || configStore.defaultLatitude,
      lng: coordinate.lng || configStore.defaultLongitude,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unnecessary dependency
  }, [coordinate.lat, coordinate.lng]);

  return (
    <>
      <Sheet
        className="h-full max-h-screen rounded-none px-0 md:w-[480px] md:rounded-l-2xl"
        footer={
          <Footer
            formattedAddress={formattedAddress}
            handleDeliveryClick={handleDeliveryClick}
            isAddressSelected={isAddressSelected}
            isAreaSupported={isAreaSupported}
            t={t}
          />
        }
        id="address-map-sheet"
        isTitleBorder={false}
        onOpenChange={handleSheetState}
        open={open}
        preventCloseOnOutsideInteraction
        title={
          isAddressSelected
            ? t("addressJourney.confirmDeliveryLocation")
            : t("addressJourney.addDeliveryLocation")
        }
      >
        <div className="flex w-full flex-col justify-between overflow-hidden text-center">
          <MapSearchBar
            isAddressSelected={isAddressSelected}
            onChangeHandler={onChangeHandler}
            searchBarRef={searchBarRef}
            t={t}
          />

          <CenteredMapIllustration isTyping={isTyping} />

          <AddressSelectorContainer
            coordinates={coordinates}
            countryOfOperationID={countryOfOperationID}
            formattedAddress={formattedAddress}
            id={id}
            isAddressSelected={isAddressSelected}
            searchBarRef={searchBarRef}
            setAddressInfo={setAddressInfo}
            setCoordinates={setCoordinates}
            setEmirate={setEmirate}
            setIsAddressSelected={setIsAddressSelected}
            setIsAreaSupported={setIsAreaSupported}
            setTown={setTown}
            t={t}
          />
        </div>
      </Sheet>

      {isUserLoggedIn ? (
        <AddressForm
          address={addressData}
          coordinates={coordinates}
          editInfo={editInfo}
          openForm={showForm}
          setIsSelected={handleSetIsSelected}
          setIsTyping={handleSetIsTyping}
          setShowMap={setShowMap}
          storeId={storeId}
          t={t}
          toggleForm={toggleAddressForm}
        />
      ) : null}
    </>
  );
};
